import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import { closeSliderCart } from '../../sections/slider-cart';
import { closeMobileFilterNav } from '../../components/filtering-sorting/mobileFilters';
import { initAccordion } from '../../components/accordion';

const TAP_GHOST_CLICK_DELAY = 500;

// Query a single element with an optional root node.
const qs = (selector, root = document) => root.querySelector(selector);
// Query all matching elements and always return a plain array.
const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

// Get element outer height and optionally include vertical margins.
const getOuterHeight = (element, includeMargin = false) => {
  if (!element) {
    return 0;
  }

  const height = element.offsetHeight;
  if (!includeMargin) {
    return height;
  }

  const styles = window.getComputedStyle(element);
  const marginTop = Number.parseFloat(styles.marginTop) || 0;
  const marginBottom = Number.parseFloat(styles.marginBottom) || 0;
  return height + marginTop + marginBottom;
};

// Attach delegated event handling on document for dynamic nodes.
const bindDelegated = (eventName, selector, handler) => {
  document.addEventListener(eventName, (e) => {
    const target = e.target.closest(selector);
    if (!target) {
      return;
    }

    handler(e, target);
  });
};

let lastTouchTimestamp = 0;

const syncMobileNavA11y = (isOpen) => {
  const mobileNav = qs('[data-mobile-nav]');
  if (!mobileNav) {
    return;
  }

  mobileNav.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

  if (isOpen) {
    mobileNav.removeAttribute('inert');
  } else {
    mobileNav.setAttribute('inert', '');
  }

  qsa('a, button, input, select, textarea, [tabindex]', mobileNav).forEach((el) => {
    if (isOpen) {
      if (el.hasAttribute('data-mobile-nav-prev-tabindex')) {
        const previous = el.getAttribute('data-mobile-nav-prev-tabindex');
        if (previous === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', previous);
        }
        el.removeAttribute('data-mobile-nav-prev-tabindex');
      }
      return;
    }

    if (!el.hasAttribute('data-mobile-nav-prev-tabindex')) {
      el.setAttribute('data-mobile-nav-prev-tabindex', el.getAttribute('tabindex') || '');
    }

    el.setAttribute('tabindex', '-1');
  });
};

// Bind touch and click as a single tap interaction with ghost-click protection.
const bindTap = (selector, handler) => {
  bindDelegated('touchend', selector, (e, target) => {
    lastTouchTimestamp = Date.now();
    handler(e, target);
  });

  bindDelegated('click', selector, (e, target) => {
    if (Date.now() - lastTouchTimestamp < TAP_GHOST_CLICK_DELAY) {
      return;
    }

    handler(e, target);
  });
};

// Recompute and position the mobile nav bottom block under the opened subnav.
const adjustBottomElement = (subnav) => {
  const calculated_subnav_height = getOuterHeight(qs('.mobile-subnav__parent', subnav), true)
    + getOuterHeight(qs('.mobile-nav__level-2', subnav), true)
    + getOuterHeight(qs('.mobile-subnav__footer-image', subnav), true);

  const mobileBottom = qs('[data-mobile-bottom]');
  if (!mobileBottom) {
    return;
  }

  const mobileTopHeight = getOuterHeight(qs('[data-mobile-top]'), true);
  const bottomContainerHeight = getOuterHeight(qs('[data-mobile-bottom] .mobile-nav-bottom__container'), true);

  mobileBottom.style.top = `${calculated_subnav_height + mobileTopHeight}px`;
  mobileBottom.style.height = `${bottomContainerHeight}px`;
};

// Open a level-2 mobile subnav and update related accessibility attributes.
const openMobileSubNav = (item) => {
  const handle = item.dataset.handle;
  const subnav = qs(`.mobile-subnav[data-parent="${handle}"]`);

  if (!subnav) {
    return;
  }

  adjustBottomElement(subnav);
  subnav.classList.add('opened');

  const mobileNav = qs('[data-mobile-nav]');
  mobileNav?.classList.add('level-2-opened');
  if (mobileNav) {
    mobileNav.scrollTop = 0;
  }

  qsa('.mobile-nav__level-1 [data-has-children]').forEach((mobileItem) => {
    mobileItem.setAttribute('role', 'menuitem');
    mobileItem.setAttribute('aria-expanded', 'false');
  });

  item.setAttribute('aria-expanded', 'true');
  subnav.setAttribute('aria-live', 'polite');
};

// Close the currently opened level-2 subnav and restore level-1 state.
const closeMobileSubNav = (subnav) => {
  qs('[data-mobile-bottom]')?.removeAttribute('style');
  subnav.classList.add('closing');
  qs('[data-mobile-nav]')?.classList.remove('level-2-opened');

  window.setTimeout(() => {
    subnav.classList.remove('closing', 'opened');

    const mobileNav = qs('[data-mobile-nav]');
    if (mobileNav) {
      mobileNav.scrollTop = 0;
    }

    qs('.mobile-subnav__parent', subnav)?.setAttribute('aria-expanded', 'false');
  }, 300);
};

// Open the mobile navigation drawer and optionally focus search.
const openMobileNav = (search_focus) => {
  if (window.theme.breakpoints.current !== 'mobile') {
    return;
  }

  // Fix a iOS Safari bug to reset the scrollTop
  const mobileNav = qs('[data-mobile-nav]');
  if (!mobileNav) {
    return;
  }

  mobileNav.style.position = 'absolute';
  mobileNav.classList.add('visible');

  window.setTimeout(() => {
    mobileNav.removeAttribute('style');
    mobileNav.scrollTop = 0;
    document.documentElement.classList.add('mobile-nav-opened');
    disableBodyScroll(mobileNav);
    syncMobileNavA11y(true);
    mobileNav.focus();

    if (search_focus) {
      qs('#searchMobile')?.focus();
    }
  }, 50);
};

// Close the mobile navigation drawer, with optional force close on non-mobile breakpoints.
const closeMobileNav = (force = false) => {
  if (window.theme.breakpoints.current !== 'mobile' && !force) {
    return;
  }

  document.documentElement.classList.remove('mobile-nav-opened');

  const mobileNav = qs('[data-mobile-nav]');
  if (mobileNav) {
    enableBodyScroll(mobileNav);
  }

  const openedSubnav = qs('.opened[data-mobile-subnav]');
  if (openedSubnav) {
    closeMobileSubNav(openedSubnav);
  }

  syncMobileNavA11y(false);

  qs('[data-open-mobile-nav]')?.focus();

  window.setTimeout(() => {
    mobileNav?.classList.remove('visible');
  }, 300);
};

/*
const injectFeaturedItems = () => {
  if (!$('[data-mobile-nav]').data('show-featured')) {
    $('[data-mobile-bottom]').remove();
    return;
  }

  let inject_html = '';
  _.each($('[data-main-nav] [data-mega-featured-item]'), (item) => {
    inject_html += `
      <div class="featured-item" onClick="window.location.href='${$(item).find('a').attr('href')}';">
        ${$(item).find('noscript').text().replace('2048x2048', '280x280')}
        ${$(item).find('a')[0].outerHTML}
      </div>
    `;
  });
  $('[data-mobile-bottom]').append(`<li class="featured-items">${inject_html}</li>`);
};
*/

// Initialize all mobile nav event bindings once.
const initMobileNav = () => {
  // injectFeaturedItems();

  if (initMobileNav.initialized) {
    return;
  }
  initMobileNav.initialized = true;

  syncMobileNavA11y(false);

  bindDelegated('click', '[data-mobile-nav-open]', () => {
    const search_focus = false;
    // if ($item.hasClass('mobile-option--search')) {
    //   search_focus = true;
    // }

    (document.documentElement.classList.contains('mobile-nav-opened')) ? closeMobileNav() : openMobileNav(search_focus);
  });

  // [ADA]
  if (qsa('[data-mobile-bottom-accordion]').length) {
    initAccordion('[data-mobile-bottom-accordion]');

    qsa('[data-mobile-bottom-accordion]').forEach((accordion) => {
      accordion.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          accordion.click();
        }
      });
    });
  }

  // [ADA]
  qs('[data-mobile-nav-open]')?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.target.click();
    }
  });

  bindTap('[data-full-overlay]', (e) => {
    e.stopPropagation();
    e.preventDefault();
    closeMobileNav(true);
    closeSliderCart();
    closeMobileFilterNav();
  });

  bindDelegated('click', '.mobile-nav__level-1 [data-has-children]', (_e, target) => {
    openMobileSubNav(target);
  });

  // Close mobile subnav
  bindDelegated('click', '.opened[data-mobile-subnav] .mobile-subnav__parent', (_e, target) => {
    const subnav = target.parentElement;
    if (!subnav) {
      return;
    }

    closeMobileSubNav(subnav);
  });

  window.addEventListener('breakpointHasChanged', (e) => {
    const current_breakpoint = e.detail;
    if (current_breakpoint !== 'mobile' && document.documentElement.classList.contains('mobile-nav-opened')) {
      closeMobileNav(true);
    }
  });
};

initMobileNav.initialized = false;

export { initMobileNav, closeMobileNav };
